import type {Optional} from '@yandex-delivery/types'
import {applySpec, constant, firstOf, flow, getUUID, identity, ifElse, map} from '@yandex-delivery/utils'

import {type Suggest, type SuggestItem, SuggestType} from '../../../model/suggest'
import {phoenixSuggestGetter, suggestObjectGetter} from '../../../repo/suggest/getters'
import type {SuggestBody, SuggestObject, SuggestResponse} from '../../../repo/suggest/types'

// Getters
const getResults: (response: SuggestResponse) => Array<SuggestObject> = phoenixSuggestGetter('results')
const getTitle: (suggestObject: SuggestObject) => string = suggestObjectGetter('title.text')
const getSubTitle: (suggestObject: SuggestObject) => string = suggestObjectGetter('subtitle.text')
const getDistance: (suggestObject: SuggestObject) => Optional<string> = suggestObjectGetter('distance.text')
const getUri: (suggestObject: SuggestObject) => Optional<string> = suggestObjectGetter('uri')
const getAddress = (suggestObject: SuggestObject) =>
  [suggestObject.city, getTitle(suggestObject)].filter(Boolean).join(', ')

// Constants
const toFinalType: () => SuggestType.FINAL = constant(SuggestType.FINAL)
const toInProgressType: () => SuggestType.IN_PROGRESS = constant(SuggestType.IN_PROGRESS)

// Checkers
const isFinalizeAction: (body: SuggestBody) => boolean = (body: SuggestBody) => {
  if ('action' in body) {
    return body.action === 'finalize'
  }
  return false
}

// Mappers
const toDefaultUniqueID = (suggestObject: SuggestObject): string => `${suggestObject.position}.${suggestObject.text}`
const toUniqueID: (suggestObject: SuggestObject) => string = flow(firstOf(getUri, toDefaultUniqueID), getUUID)
const toAddressSuggestType: (body: SuggestBody) => SuggestType = ifElse(isFinalizeAction, toFinalType, toInProgressType)
const toAddress: (suggestObject: SuggestObject) => SuggestItem = applySpec<SuggestItem>({
  __origin: identity,
  id: toUniqueID,
  title: getTitle,
  subtitle: getSubTitle,
  distance: getDistance,
  address: getAddress,
})
const toAddressSuggest: (response: SuggestResponse) => Array<SuggestItem> = flow(getResults, map(toAddress))
const toBaseSuggest: (response: SuggestResponse) => Suggest = applySpec<Suggest>({
  __origin: identity,
  type: toInProgressType,
  suggest: toAddressSuggest,
})

export function toAddressesSuggest(body: SuggestBody) {
  return function (response: SuggestResponse): Suggest {
    return {
      ...toBaseSuggest(response),
      type: toAddressSuggestType(body),
    }
  }
}
